<script>
import Component from 'vue-class-component';
import ChipGroup from '@optimizer/components/ChipGroup.vue';
import { Prop } from 'vue-property-decorator';
import { MapGetters } from '@core/helpers/decorators';
import { ProductRecommendation } from '../models';

@Component
@MapGetters(['pointOfSaleSettings', 'groupList'])
export default class ProductPriceChips extends ChipGroup {
  @Prop({
    required: false,
    validator(value) {
      return value instanceof ProductRecommendation || value === false;
    }
  }) mainProductRecommendation;

  @Prop({ type: Boolean, default: false }) disabled;

  get useSmallChips() {
    return this.$vuetify.breakpoint.smAndDown;
  }

  get pricesMapsByDemand() {
    return this.getPricesMapsByDemand(this.groupInfo);
  }

  get mainProductPricesMapsByDemand() {
    if (!this.mainProductRecommendation) return null;
    return this.getPricesMapsByDemand(this.mainProductRecommendation);
  }

  parseGroupInfo(productRecommendation) {
    const parsedInfo = {};
    const pricesValid = [];
    this.groupList.forEach((group) => {
      const priceValid = this.arePricesValid(group.code_name);
      pricesValid.push(priceValid);
      parsedInfo[group.code_name] = {
        label: productRecommendation.getPriceRangeLabel(group.code_name, productRecommendation.confirmed),
        emitName: 'group-click',
        emitArgs: { productRecommendationGuid: productRecommendation.guid },
        disabled: this.disabled,
        clickDisabled: false,
        borderColor: !priceValid && 'red'
      };
    });

    // Emit validation result to parent for side effects
    const allPricesValidObject = pricesValid.every((valid) => valid);
    this.$emit('prices-valid', allPricesValidObject);
    return parsedInfo;
  }

  arePricesValid(groupName) {
    const areMinMaxValid = this.areMinMaxPricesValid(groupName);
    let isHalfPortionRateValid = true;
    if (this.mainProductRecommendation) {
      isHalfPortionRateValid = this.isHalfPortionRateValid(groupName);
    }
    return areMinMaxValid && isHalfPortionRateValid;
  }

  areMinMaxPricesValid(groupName) {
    // Demand info is a ProductRecommendation object
    const max = this.groupInfo.max_range_price || Infinity;
    const min = this.groupInfo.min_range_price || 0.0;
    const pricesMap = this.pricesMapsByDemand.get(groupName);
    if (this.groupInfo.confirmed) return [...pricesMap.values()].every(({ price }) => price <= max && price >= min);
    return true; // If prices are not confirmed, we dont show red border
  }

  isHalfPortionRateValid(groupName) {
    // Demand info is a ProductRecommendation object
    const pricesMap = this.pricesMapsByDemand.get(groupName);
    const mainProductPricesMap = this.mainProductPricesMapsByDemand.get(groupName);
    const priceObjKey = this.groupInfo.confirmed ? 'price' : 'previous_price';
    return [...pricesMap.entries()].every(([key, priceObj]) => {
      // Get the price for the same service and sale rate
      const mainPriceObj = mainProductPricesMap.get(key);
      if (!mainPriceObj) return true;
      // Get main product and subordinate product price
      const parentPrice = mainPriceObj[priceObjKey];
      const price = priceObj[priceObjKey];
      // Check half portion is valid
      const maxAllowedPrice = this.pointOfSaleSettings.maxPriceHalfPortionRate * parentPrice;
      return price <= maxAllowedPrice;
    });
  }

  getPricesMapsByDemand(productRecommendation) {
    const pricesByDemand = new Map();
    this.groupList.forEach((group) => {
      const { code_name: groupName } = group;
      let pricesMap = new Map();
      if (productRecommendation.confirmed) {
        // List of objects with prices
        pricesMap = productRecommendation.getPricesMap(groupName);
      }
      pricesByDemand.set(groupName, pricesMap);
    });
    return pricesByDemand;
  }
}
</script>
